import { ApplyDialog } from '@core/components/ApplyWindow/ApplyWindow'
import { getLocalStorageItem, setLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import dayjs from 'dayjs'
import React, { createContext, useContext, useEffect, useState } from 'react'

interface IsApplyDialogContextType {
    isApplyDialog: boolean
    resized: boolean
    open: (id: string, job: Job) => void
    close: () => void
    collapseDialog: () => void
    error: boolean
    expiredDate: string
}

type Job = {
    link: string
    title: string
    expiredDate: string
}

const applyDialogStorageData = getLocalStorageItem('apply_dialog', {
    opened: false,
    resized: false,
    expiredDate: '',
    job: {
        link: '',
        title: '',
        expiredDate: '',
    },
})

const IsApplyDialogContext = createContext<IsApplyDialogContextType>({
    isApplyDialog: applyDialogStorageData?.opened ?? false,
    open: () => {},
    close: () => {},
    resized: applyDialogStorageData?.resized ?? false,
    collapseDialog: () => {},
    error: false,
    expiredDate: applyDialogStorageData?.job?.expiredDate ?? '',
})

export const IsApplyDialogProvider = ({ children }: { children: React.ReactNode }) => {
    const expiredDate = getLocalStorageItem('apply_dialog')?.job?.expiredDate ?? ''
    const [isApplyDialog, setIsApplyDialog] = useState(getLocalStorageItem('apply_dialog')?.opened ?? false)
    const [resized, setResized] = useState(getLocalStorageItem('apply_dialog')?.resized ?? false)
    const [error, setError] = useState<boolean>(false)

    const close = () => {
        setIsApplyDialog(false)
        setLocalStorageItem('apply_dialog', { opened: false })
    }

    const open = (id: string, job: Job) => {
        const oldId = getLocalStorageItem('apply_dialog')?.id ?? ''
        if (oldId && oldId === id) {
            setError(true)

            setTimeout(() => {
                setError(false)
            }, 3000)
        } else {
            setIsApplyDialog(true)
            let newObject = {
                opened: true,
                id: id,
                job: job,
                resized: false,
                expiredDate: job?.expiredDate,
            }
            setResized(false)
            setLocalStorageItem('apply_dialog', newObject)
            document.querySelector('.p-dialog-content')?.classList.remove('p-dialog-hidden')
            document.querySelector('.p-dialog')?.classList.remove('p-dialog-content-hidden')
        }
    }

    const changeLocalStorageData = () => {
        const object = getLocalStorageItem('apply_dialog', { opened: true, resized: false })
        const newObject = {
            opened: true,
            id: object?.id,
            job: object?.job,
            expiredDate: object?.job?.expiredDate,
            resized: !object?.resized,
        }
        setResized(!object?.resized)
        setLocalStorageItem('apply_dialog', newObject)
    }

    const collapseDialog = () => {
        changeLocalStorageData()
        if (resized) {
            document.querySelector('.p-dialog-content')?.classList.add('p-dialog-hidden')
            document.querySelector('.p-dialog')?.classList.add('p-dialog-content-hidden')
        } else {
            document.querySelector('.p-dialog-content')?.classList.remove('p-dialog-hidden')
            document.querySelector('.p-dialog')?.classList.remove('p-dialog-content-hidden')
        }
    }

    useEffect(() => {
        if (dayjs(expiredDate).diff(dayjs(), 'day') < 0) {
            close()
        }
    }, [expiredDate])

    return (
        <IsApplyDialogContext.Provider
            value={{ isApplyDialog, open, close, resized, collapseDialog, error, expiredDate }}
        >
            {<ApplyDialog isActive={isApplyDialog} onHide={close} />}
            {children}
        </IsApplyDialogContext.Provider>
    )
}

export const useIsApplyDialog = () => {
    return useContext(IsApplyDialogContext)
}
